import React from 'react';
import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { QuoteProvider } from './QuoteContext';
import { TooltipProvider } from '@radix-ui/react-tooltip';
import { ToastProvider } from './CustomCalendarToast';
import { AuthProvider, useAuth } from './AuthContext';
import QuoteDashboard from './QuoteDashboard';
import ManagerDashboard from './ManagerDashboard';
import SharedLayout from './SharedLayout';
import SolarQuotePro from './SolarQuotePro';
import ClientInfo from './ClientInfo';
import CompanyInfo from './CompanyInfo';
import QuoteForm from './QuoteForm';
import ConfirmationPage from './ConfirmationPage';
import CalendarTasks from './CalendarTasks';
import LoginPage from './LoginPage';
import ChangePassword from './ChangePassword';
import InvoiceDashboard from './InvoiceDashboard';

import './index.css';
import './styles.css';
import './App.css';

const steps = [
  'Informations Client',
  'Informations Entreprise',
  'Formulaire de Devis',
  'Confirmation'
];

// ── Route protégée ─────────────────────────────────────────────────────────
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6b7280' }}>Chargement...</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

// ── Route manager uniquement ───────────────────────────────────────────────
const ManagerRoute = ({ children }) => {
  const { user, profile, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (profile && profile.role !== 'manager') return <Navigate to="/quote-dashboard" replace />;
  return children;
};

function AppRoutes() {
  const location = useLocation();
  return (
    <TransitionGroup>
      <CSSTransition key={location.key} classNames="fade" timeout={300}>
        <Routes location={location}>
          {/* Auth */}
          <Route path="/login" element={<LoginPage />} />

          {/* App protégée */}
          <Route path="/" element={<ProtectedRoute><SolarQuotePro /></ProtectedRoute>} />
          <Route path="/change-password" element={<ProtectedRoute><ChangePassword /></ProtectedRoute>} />

          {/* Formulaire devis */}
          <Route path="/client-info" element={<ProtectedRoute><SharedLayout currentStep={0} steps={steps}><ClientInfo currentStep={0} steps={steps} /></SharedLayout></ProtectedRoute>} />
          <Route path="/company-info" element={<ProtectedRoute><SharedLayout currentStep={1} steps={steps}><CompanyInfo /></SharedLayout></ProtectedRoute>} />
          <Route path="/quote-form" element={<ProtectedRoute><SharedLayout currentStep={2} steps={steps}><QuoteForm currentStep={2} steps={steps} /></SharedLayout></ProtectedRoute>} />
          <Route path="/confirmation" element={<ProtectedRoute><SharedLayout currentStep={3} steps={steps}><ConfirmationPage /></SharedLayout></ProtectedRoute>} />

          {/* Dashboards */}
          <Route path="/quote-dashboard" element={<ProtectedRoute><QuoteDashboard /></ProtectedRoute>} />
          <Route path="/manager-dashboard" element={<ManagerRoute><ManagerDashboard /></ManagerRoute>} />
          <Route path="/invoice-dashboard" element={<ProtectedRoute><InvoiceDashboard /></ProtectedRoute>} />

          {/* Divers */}
          <Route path="/calendar-tasks" element={<ProtectedRoute><CalendarTasks selectedDate={new Date()} quotes={[]} /></ProtectedRoute>} />

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </CSSTransition>
    </TransitionGroup>
  );
}

function App() {
  return (
    <AuthProvider>
      <QuoteProvider>
        <TooltipProvider>
          <ToastProvider>
            <div className="App">
              <AppRoutes />
            </div>
          </ToastProvider>
        </TooltipProvider>
      </QuoteProvider>
    </AuthProvider>
  );
}

export default App;
